"use client";

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PLATFORM_COLORS } from "./platform-colors";
import type { SocialPlatform, ScheduledPostStatus } from "@content-os/config";

interface CalendarFiltersProps {
  selectedPlatforms: SocialPlatform[];
  onPlatformToggle: (platform: SocialPlatform) => void;
  selectedStatuses: ScheduledPostStatus[];
  onStatusToggle: (status: ScheduledPostStatus) => void;
  view: "month" | "week" | "day";
  onViewChange: (view: "month" | "week" | "day") => void;
  availablePlatforms?: SocialPlatform[];
}

const DEFAULT_PLATFORMS: SocialPlatform[] = [
  "INSTAGRAM", "TIKTOK", "FACEBOOK", "YOUTUBE",
  "LINKEDIN", "TWITTER", "THREADS", "PINTEREST",
];

const ALL_STATUSES: ScheduledPostStatus[] = [
  "SCHEDULED", "PUBLISHED", "FAILED", "CANCELLED",
];

export function CalendarFilters({
  selectedPlatforms,
  onPlatformToggle,
  selectedStatuses,
  onStatusToggle,
  view,
  onViewChange,
  availablePlatforms,
}: CalendarFiltersProps) {
  const { t } = useTranslation("calendar");
  const platforms =
    availablePlatforms && availablePlatforms.length > 0
      ? availablePlatforms
      : DEFAULT_PLATFORMS;
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground uppercase">{t("filters.platform")}</span>
        {platforms.map((p) => (
          <Badge
            key={p}
            variant={selectedPlatforms.includes(p) ? "default" : "outline"}
            className="cursor-pointer text-xs"
            style={selectedPlatforms.includes(p) ? { backgroundColor: PLATFORM_COLORS[p] } : undefined}
            onClick={() => onPlatformToggle(p)}
          >
            {t(`platform.${p}`, { defaultValue: p.charAt(0) + p.slice(1).toLowerCase() })}
          </Badge>
        ))}
        <span className="text-xs font-medium text-muted-foreground uppercase ml-2">{t("filters.status")}</span>
        {ALL_STATUSES.map((s) => (
          <Badge
            key={s}
            variant={selectedStatuses.includes(s) ? "default" : "outline"}
            className="cursor-pointer text-xs"
            onClick={() => onStatusToggle(s)}
          >
            {t(`status.${s}`, { defaultValue: s.charAt(0) + s.slice(1).toLowerCase() })}
          </Badge>
        ))}
      </div>

      <div className="flex items-center gap-1 border rounded-md p-0.5">
        {(["month", "week", "day"] as const).map((v) => (
          <Button
            key={v}
            variant={view === v ? "default" : "ghost"}
            size="sm"
            className="h-7 text-xs capitalize"
            onClick={() => onViewChange(v)}
          >
            {t(`views.${v}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}
